import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
} from 'react';
import styled from 'styled-components';
import { ApplicantMainInfo } from './ApplicantMainInfo';
import { ApplicantServices } from './ApplicantServices';
import { ApplicantDates } from './ApplicantDates';
import { ApplicantDocuments } from './ApplicantDocuments';
import { useFormikContext } from 'formik';
import {
  getApplicantDocumentFilter,
  getVisaTypes,
} from 'helpers/api-requests/admin';
import { useTranslation } from 'react-i18next';
// import { StyledEmptyButton } from 'pages/Tariff/TariffServiceRow';
import { Button } from 'reactstrap';
import { optionalField } from 'forms/shortcuts';
import { NO } from 'common/data/constants';
import ToggleSelector from 'components/Common/ToggleSelector'; // Import the ToggleSelector
import HelpText from 'components/HelpText';
import { isUserOperator } from 'helpers/jwt_helper';

const StyledWrapper = styled.div`
  width: 100%;
  margin-top: 0.5rem;
`;

const FooterBlock = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  margin-top: 2rem;
  padding-top: 1.25rem;
  border-top: 1px solid ${({ theme }) => theme.colors.gray100 ?? '#e2e8f0'};
`;

const ContinueButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  padding: 0.625rem 1.75rem;
  font-size: 14px;
  font-weight: 600;

  color: #fff;
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 999px;

  transition: background-color 0.2s ease, box-shadow 0.2s ease,
    transform 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) =>
      theme.colors.primaryDark ?? theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 6px 16px ${({ theme }) => theme.colors.primary}40;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}55;
  }

  &:disabled,
  &.btn.disabled {
    background: ${({ theme }) => theme.colors.gray100 ?? '#f1f5f9'};
    color: ${({ theme }) => theme.colors.gray400};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ContentWrapper = styled.div`
  margin-top: 1.5rem;
`;

export const ApplicantBody = forwardRef(
  (
    {
      index,
      useFieldPreviousVisa,
      useFieldPreviousUssr,
      orderTypeFieldsSettings,
    },
    ref,
  ) => {
    const { values, errors } = useFormikContext();
    const isRequestSent =
      values?.applicants?.[index]?.statusApplicant == 'request_sent';
    const { t } = useTranslation();
    const [isDisabledDate, setIsDisabledDate] = useState(true);
    const [currentActiveTab, setCurrentActiveTab] = useState('mainInformation');
    const [visaTypes, setVisaTypes] = useState([]);
    const applicantContainer = document.getElementById(
      `applicant-${index + 1}`,
    );

    const handleChangeTab = tab => {
      if (currentActiveTab !== tab) {
        setCurrentActiveTab(tab);
      }
    };

    useEffect(() => {
      getVisaTypes({
        country: values.client.country,
        valid_nationalities:
          values?.applicants?.[index].applicantDetail.citizenship,
      }).then(visas => {
        if (visas.success) {
          setVisaTypes(visas.data.results);
        }
      });
    }, [
      values.client.country,
      values?.applicants?.[index].applicantDetail.citizenship,
    ]);

    const currentVisa = useMemo(
      () =>
        visaTypes.find(visa => visa.id === values.applicants?.[index].visaType),
      [visaTypes, values.applicants?.[index].visaType],
    );

    // Disable 'Services' tab
    const isDisabledServices = useMemo(
      () =>
        (orderTypeFieldsSettings?.enableVisaType &&
          !values.applicants?.[index]?.visaType) ||
        (orderTypeFieldsSettings?.enableVisaType &&
          !values.applicants?.[index]?.visaUrgency) ||
        (orderTypeFieldsSettings?.enableVisaType &&
          currentVisa?.durations.length > 0 &&
          !values.applicants?.[index]?.visaDuration) ||
        !values.applicants?.[index]?.visaUrgency ||
        (orderTypeFieldsSettings?.enableDeliveryMethod &&
          !values.applicants?.[index]?.deliveryMethod),
      [
        values.applicants?.[index]?.visaType,
        values.applicants?.[index]?.visaUrgency,
        values.applicants?.[index]?.visaDuration,
        values.applicants?.[index]?.deliveryMethod,
        values.applicants?.[index]?.hasResidency,
        currentVisa,
      ],
    );

    // Disable 'Documents' tab
    const isDisabledDocuments = useMemo(
      () =>
        values.applicants?.[index]?.services?.length === 0 ||
        values.applicants?.[index]?.hasResidency === NO,
      [
        values.applicants?.[index]?.services,
        values.applicants?.[index]?.hasResidency,
      ],
    );

    // Disable 'Dates' tab
    const getDisabledDates = useCallback(() => {
      getApplicantDocumentFilter({
        order_type: values.orderType,
        number: values.orderDetail?.number ?? null,
        client_id: values.client.client,
        client_type: values.client.clientType,
        nationality: values.applicants[index].applicantDetail.citizenship,
        applicant: values.applicants[index].applicantDetail.id,
        country: values.client.country,
        previous_visa: values.applicants[index].previusVisa,
        visa_type: values.applicants[index].visaType,
        visa_duration: optionalField(values.applicants[index].visaDuration),
        products: values.applicants[index].services.map(
          service => service.product_id,
        ),
      })
        .then(response => {
          if (response.success) {
            const required_docs_count = response.data.documents.results.filter(
              doc => doc.options.ALLOW_MULTIPLE === false,
            ).length;

            const uploaded_required_docs_count =
              response.data.documents.results.filter(
                doc =>
                  (!doc.options.ALLOW_MULTIPLE &&
                    doc.options.IS_REUSABLE &&
                    doc.profile_document !== null) ||
                  (!doc.options.ALLOW_MULTIPLE &&
                    !doc.options.IS_REUSABLE &&
                    values.applicants?.[index].files.find(
                      applicant_file =>
                        applicant_file.file.document.id === doc.id,
                    )),
              ).length;

            const required_docs_are_uploaded =
              required_docs_count === uploaded_required_docs_count;

            setIsDisabledDate(
              isDisabledDocuments || !required_docs_are_uploaded,
            );
          }
        })
        .catch(() => {
          setIsDisabledDate(
            isDisabledDocuments ||
              values.applicants?.[index].files.length === 0,
          );
        });
    });

    useEffect(() => {
      getDisabledDates();
    }, [values.applicants?.[index].files, isDisabledDocuments]);

    // Whether 'Main information' tab has errors
    const isMainError = useMemo(
      () =>
        !!errors.applicants?.[index]?.visaType ||
        !!errors.applicants?.[index]?.deliveryMethod ||
        !!errors.applicants?.[index]?.address ||
        !!errors.applicants?.[index]?.addressAnotherApplicant,
      [
        errors.applicants?.[index]?.visaType,
        errors.applicants?.[index]?.deliveryMethod,
        errors.applicants?.[index]?.address,
        errors.applicants?.[index]?.addressAnotherApplicant,
      ],
    );

    // Define tab options for ToggleSelector
    const tabOptions = useMemo(() => {
      return [
        {
          value: 'mainInformation',
          label: t('order.mainInformation'),
          icon: 'bx bx-user',
          disabled: false,
        },
        {
          value: 'services',
          label: t('sideBar.services'),
          icon: 'bx bx-briefcase',
          disabled: isRequestSent || isDisabledServices,
          disabledTooltip: t('order.completeMainInfoFirst', {
            defaultValue: 'Complete main information first',
          }),
        },
        {
          value: 'documents',
          label: t('common.documents'),
          icon: 'bx bx-file',
          disabled: isRequestSent || isDisabledDocuments,
          disabledTooltip: t('order.addServicesFirst', {
            defaultValue: 'Add services first',
          }),
        },
        {
          value: 'dates',
          label: t('common.datesTab'),
          icon: 'bx bx-calendar',
          disabled: isRequestSent || isDisabledDate,
          disabledTooltip: t('order.uploadDocumentsFirst', {
            defaultValue: 'Upload required documents first',
          }),
        },
      ];
    }, [
      t,
      isDisabledServices,
      isDisabledDocuments,
      isDisabledDate,
      isRequestSent,
    ]);

    const tabs = useMemo(
      () => ({
        mainInformation: {
          key: 'mainInformation',
          content: (
            <ApplicantMainInfo
              ref={ref}
              currentVisa={currentVisa}
              index={index}
              visaTypes={visaTypes}
              useFieldPreviousVisa={useFieldPreviousVisa}
              useFieldPreviousUssr={useFieldPreviousUssr}
              orderTypeFieldsSettings={orderTypeFieldsSettings}
              isRequestSent={isRequestSent}
            />
          ),
          title: 'order.mainInformation',
          next: 'services',
          error: isMainError,
          disabled: false,
        },
        services: {
          key: 'services',
          content: <ApplicantServices index={index} />,
          disabled: isDisabledServices,
          title: 'sideBar.services',
          next: 'documents',
          prev: 'mainInformation',
        },
        documents: {
          key: 'documents',
          content: <ApplicantDocuments index={index} />,
          title: 'common.documents',
          disabled: isDisabledDocuments,
          next: 'dates',
          prev: 'services',
        },
        dates: {
          key: 'dates',
          content: (
            <ApplicantDates
              index={index}
              orderTypeFieldsSettings={orderTypeFieldsSettings}
            />
          ),
          disabled: isDisabledDate,
          title: 'common.datesTab',
          prev: 'documents',
        },
      }),
      [
        index,
        isDisabledServices,
        currentVisa,
        visaTypes,
        isDisabledDocuments,
        isDisabledDate,
        isMainError,
        orderTypeFieldsSettings,
      ],
    );

    // Handle tab change with disabled check
    const handleTabChange = tabKey => {
      // Check if the tab is disabled
      if (tabs[tabKey]?.disabled) {
        return;
      }
      handleChangeTab(tabKey);
    };

    const handleSetNextTab = () => {
      applicantContainer?.scrollIntoView(true);
      setCurrentActiveTab(tabs[currentActiveTab].next);
    };
    return (
      <div className="d-flex flex-column">
        <StyledWrapper>
          <ToggleSelector
            options={tabOptions}
            value={currentActiveTab}
            onChange={handleTabChange}
            maxWidth="100%"
            padding="8px 16px"
            fontSize="13px"
            fontWeight="600"
            gap="8px"
            iconSize="16px"
            theme="muted"
          />
          {/* Show HelpText when simplified mode is active */}
          {isRequestSent && isUserOperator && (
            <HelpText
              variant="warning"
              size="sm"
              showLeftBorder
              leftBorderColor="#f59e0b"
              marginTop="8px"
              icon="bx bx-lock-alt"
            >
              {t('order.tabsLockedInRequestMode')}
            </HelpText>
          )}
        </StyledWrapper>

        <ContentWrapper>{tabs[currentActiveTab].content}</ContentWrapper>

        {tabs[currentActiveTab]?.next && !isRequestSent && (
          <FooterBlock>
            <ContinueButton
              type="button"
              disabled={tabs[tabs[currentActiveTab].next].disabled}
              onClick={handleSetNextTab}
            >
              {t('common.continue')}
            </ContinueButton>
          </FooterBlock>
        )}
      </div>
    );
  },
);
